import React, { memo } from 'react';

import { PaymentDto } from '@alpha/payments-dtos';
import { Box, Button } from '@alpha/ui-lib/ui/external';
import { DualFlag, Flag } from '@alpha/ui-lib/ui/Flag';
import { Stat } from '@alpha/ui-lib/ui/Stat';
import { StyledDivider } from '@alpha/ui-lib/ui/StyledDivider/StyledDivider';
import { APTooltip } from '@alpha/ui-lib/ui/Tooltip/APTooltip';
import { Typography } from '@alpha/ui-lib/ui/Typography';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faDownload } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import TextBoldLight from '../../../../components/TextBoldLight/TextBoldLight';
import useReportsPolling, { DownloadType, FileType, PageType } from '../../../../hooks/useReportsPolling';
import { flowToBatchStatusMapping } from '../../../../utils/batchStatus.helpers';
import { formatNumber, getCurrencySymbol } from '../../../../utils/currency.helpers';
import isPaymentBatchDraftDto from '../../../../utils/payments/isPaymentBatchDraftDto.helper';
import { IBatchCurrencyPairDecorated } from '../BatchSummary.interface';

import CurrencyAmount from './CurrencyAmount/CurrencyAmount';
import CutOffDetails from './CutOffDetails/CutOffDetails';
import Rate from './Rate/Rate';
import { useStyles } from './BatchDetails.styles';
import t from 'utils/translationHelper';

export const testIds = {
  batchStatusStat: 'batch-status-stat',
  totalPaymentStat: 'total-payments-stat',
  noOfApprovers: 'number-of-approvers-stat',
  currencyPairIcons: 'currency-pair-icons',
  cutOffTime: 'cut-off-time',
};

interface IProps {
  batchDetails: PaymentDto;
  currencyPairTab: IBatchCurrencyPairDecorated;
}

const BatchDetails: React.FC<IProps> = (props: IProps) => {
  const { batchDetails, currencyPairTab } = props;
  const { status } = batchDetails;
  const {
    noOfPayments,
    cutOffDate,
    cutOffTime,
    cutOffexpired,
    totalPayments,
    tradeId,
  } = currencyPairTab; // currencyPair currencyPairTab info

  const classes = useStyles();
  const { executeReportGeneration } = useReportsPolling(PageType.Payments);

  const handleReportGeneration = async (type: FileType, downloadType: DownloadType) => {
    await executeReportGeneration(type, tradeId, undefined, undefined, downloadType);
  };

  return (
    <div className={classes.root}>
      <div className={classes.flex}>
        {currencyPairTab.sellCurrency === currencyPairTab.buyCurrency ? (
          <Flag code={currencyPairTab.sellCurrency} size={35} showCodeLabel />
        )
          : (
            <DualFlag
              startFlag={{
                code: currencyPairTab.sellCurrency,
                size: 35,
                showCodeLabel: true,
              }}
              endFlag={{
                code: currencyPairTab.buyCurrency,
                size: 35,
                showCodeLabel: true,
              }}
              arrowHeight={9}
              arrowWidth={8}
            />
          )}
        <StyledDivider vertical />
        <Stat
          testId={testIds.totalPaymentStat}
          title={t('total_payments')}
          value={(
            <TextBoldLight
              boldText={`${noOfPayments || totalPayments} /`}
              lightText="1"
            />
          )}
          alignRight
        />
        <StyledDivider vertical />
        {flowToBatchStatusMapping.approver.includes(status)
          || flowToBatchStatusMapping.uploader.includes(status) ? (
          <CutOffDetails
            cutOffExpired={cutOffexpired}
            cutOffDate={cutOffDate}
            cutOffTime={cutOffTime}
          />
        ) : (
          <></>
        )}

        {(status === 'COMPLETED' || status === 'PROCESSING')
          && !isPaymentBatchDraftDto(batchDetails)
          && (
            <>
              {currencyPairTab.fundingAccount !== currencyPairTab.debitingAccount
                && (
                  <Box marginRight="40px">
                    <Stat
                      testId={testIds.totalPaymentStat}
                      title={t('funding_account')}
                      value={(
                        <Typography variant="h3" className="account bold">
                          {currencyPairTab.fundingAccount}
                        </Typography>
                      )}
                      alignRight
                    />
                  </Box>
                )}
              <Stat
                testId={testIds.totalPaymentStat}
                title={t('debiting_account')}
                value={(
                  <Typography variant="h3" className="account bold">
                    {currencyPairTab.debitingAccount}
                  </Typography>
                )}
                alignRight
              />
            </>
          )}
      </div>
      <div className={classes.flex}>
        {(batchDetails.status === 'PROCESSING'
          || batchDetails.status === 'COMPLETED')
          && !isPaymentBatchDraftDto(batchDetails)
          && (
            <>
              <Box marginRight="40px">
                <Stat
                  testId={testIds.totalPaymentStat}
                  title={t('debit_amount')}
                  value={(
                    <Typography variant="h3" className="debitAmount">
                      {`-${getCurrencySymbol(
                        currencyPairTab.sellCurrency,
                      )}${formatNumber(currencyPairTab.debitAmount, 2)}`}
                    </Typography>
                  )}
                />
              </Box>
              <Rate currencyPairTab={currencyPairTab} />
              <Box>
                <Stat
                  testId={testIds.totalPaymentStat}
                  title={t('credit_amount')}
                  value={(
                    <Typography variant="h3" className="creditAmount">
                      {`+${getCurrencySymbol(
                        currencyPairTab.buyCurrency,
                      )}${formatNumber(currencyPairTab.creditAmount, 2)}`}
                    </Typography>
                  )}
                  alignRight
                />
              </Box>
              <Box className={classes.buttonWrapper}>
                <APTooltip title="Download ticket">
                  <Button
                    onClick={() => handleReportGeneration(FileType.PDF, DownloadType.Fx)}
                    className={classes.button}
                  >
                    <FontAwesomeIcon className={classes.icon} size="1x" icon={faDownload as IconProp} />
                  </Button>
                </APTooltip>
              </Box>
            </>
          )}
        <CurrencyAmount
          batchDetails={batchDetails}
          currencyPairTab={currencyPairTab}
        />
      </div>
    </div>
  );
};

export default memo(BatchDetails);
